import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import IProcessed_Report from "../../../models/IProcessed_Report";
import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IUpdateReportDTO from "../../../models/DTO/IUpdateReportDTO";
import IOnUpdateReportDTO from "../../../models/DTO/IOnUpdateReportDTO";
import { buildRestUrl, buildAuthHeadersForAxios } from "../../../services/RestService";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { ReactHookInput } from "../../shared/ReactHookInput/ReactHookInput";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import { cornersOfRectangle } from "@dnd-kit/core/dist/utilities/algorithms/helpers";
import { useIntl } from "react-intl";

type Props = {
    close: (refresh?: boolean) => void;
    report: IProcessed_Report;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    setActivityIndicator: (activityIndicator: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
};

type FormData = {
    title: string;
};

const createValidationSchema = () => {
    return yup.object().shape({
        title: yup
            .string()
            .required("Title is required")
            .min(1, "Title must be at least 1 character")
            .max(255, "Title must not exceed 255 characters")
            .trim(),
    });
};

export const EditReportSidebar: React.FC<Props> = (props) => {
    const { report, notifyFn, setActivityIndicator, userModel, supplierModel } = props;
    const [isSaving, setIsSaving] = useState(false);

    const intl = useIntl();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({
        resolver: yupResolver(createValidationSchema()) as any,
        defaultValues: {
            title: report.title || "",
        },
    });

    const onSubmit = async (data: FormData) => {
        setIsSaving(true);
        setActivityIndicator(true);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.ReportsUpdateReport,
                params: { reportGuid: report.guid },
            });

            const payload: IUpdateReportDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                title: data.title.trim(),
            };

            const headers = buildAuthHeadersForAxios({
                userModel,
                supplierModel,
            });

            const response = await axios.put<IOnUpdateReportDTO>(url, payload, { headers });

            console.log("response", response);

            if (response.data && response.data.report) {
                const notification: INotification = {
                    message: "report_updated_successfully",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                };

                notifyFn(null, notification);
                props.close(true); // Close and refresh
            } else {
                const errorData: IErrorDTO = {
                    eid: "",
                    title: "error_update_report",
                    message: "Failed to update report",
                };
                notifyFn(errorData);
            }
        } catch (error: any) {
            console.error("Error updating report:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to update report";
            const errorData: IErrorDTO = {
                eid: "",
                title: "error_update_report",
                message: errorMessage,
            };
            notifyFn(errorData);
        } finally {
            setIsSaving(false);
            setActivityIndicator(false);
        }
    };

    const handleCancel = () => {
        props.close(false);
    };

    return (
        <div className="EditReportSidebar p-3">
            <OwTitle title="edit_report" leftIconName="fas fa-pencil" />

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-3">
                    <OwLabel title="title" className="form-label" />
                    <ReactHookInput
                        name="title"
                        type="text"
                        placeholder={"enter_report_title"}
                        register={register("title")}
                        error={errors.title}
                        disabled={isSaving}
                    />
                    {errors.title && <div className="text-danger small mt-1">{errors.title.message}</div>}
                </div>

                <div className="d-flex gap-2">
                    <OwButton
                        title="save"
                        type="submit"
                        className="btn-success"
                        disabled={isSaving}
                        leftIconClass={isSaving ? "fas fa-spinner fa-spin" : "fas fa-save"}
                    />
                    <OwButton title="cancel" className="btn-secondary" fn={handleCancel} disabled={isSaving} leftIconClass="fas fa-times" />
                </div>
            </form>
        </div>
    );
};
